import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button, InputText, SectionHeader, Text } from "@pentabd/ui";

import { HeaderWithNextButton, NextButton } from "@components";
import {
  ACCAuthorizePersonInfo,
  ACCWithnessInfo,
  AgreementDocument,
  VendorAuthorizePersonInfo,
  VendorWithnessInfo,
} from "./components";

import { useAgreement } from "@hooks/procurements/procurement/createProcurements/useAgreement";
import { useVendor } from "@hooks/procurements/vendors/useVendor";
import { useUser } from "@hooks/useUser";

import { getAccess } from "@helpers/permissionController";
import {
  convertMultipleDocumentData,
  revertMultipleDocumentData,
} from "@utils/documentDataConvert";
import { convertAgreementFormDataToReqData } from "@utils/procurement-agreement-data-converter";

import {
  AgreementDetailsResponse,
  AgreementType,
} from "@type/procurements/createProcurement/agreement";
import { VendorResponseType } from "@type/procurements/vendor";

import { contractAgreementValidation } from "@validations/Procurement/procurement/contractAgreementValidation";

import { FORM_FIELDS } from "@constants/form";

export const AGREEMENT_FIELDS = FORM_FIELDS.PROCUREMENT_DETAILS.AGREEMENT;

export interface AgreementPropType {
  id: any;
  changeActiveTab: (arg: number) => void;
  setIsSuccessModalOpen: (arg: boolean) => void;
  setIsErrorModalOpen: (arg: boolean) => void;
}

const Agreement = ({
  id,
  changeActiveTab,
  setIsSuccessModalOpen,
  setIsErrorModalOpen,
}: AgreementPropType) => {
  const { t } = useTranslation();

  const { user } = useUser();
  const { realm_roles: roles } = user || {};

  const { createAgreement, agreementData } = useAgreement(id);

  const { getSingleData, createError, createSuccess } =
    agreementData || ({} as AgreementDetailsResponse);

  const { data } = getSingleData || ({} as AgreementType);

  const vendorId = data && data.vendorId ? data.vendorId : "";

  const { vendorState, handleQueryById } = useVendor(false);

  const { getSingleData: vendorData } =
    vendorState || ({} as VendorResponseType);

  const method = useForm({
    resolver: yupResolver(contractAgreementValidation),
    values: revertMultipleDocumentData(data, [
      AGREEMENT_FIELDS.AGREEMENT_DOC_UPLOAD,
    ]),
  });

  const { handleSubmit } = method;

  const onSubmit = (formData: any) => {
    const reqData = { ...formData };
    reqData.vendorId = formData?.vendorId || "";

    const convertedData = convertAgreementFormDataToReqData(reqData);

    const resData = convertMultipleDocumentData(convertedData, [
      AGREEMENT_FIELDS.AGREEMENT_DOC_UPLOAD,
    ]);

    createAgreement(resData, id, data?.id);
  };

  useEffect(() => {
    if (vendorId) {
      handleQueryById(vendorId);
    }
  }, [vendorId]);

  useEffect(() => {
    switch (true) {
      case createSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case !!createError: {
        setIsErrorModalOpen(true);
        break;
      }
      default: {
        setIsSuccessModalOpen(false);
        break;
      }
    }
  }, [createSuccess, createError]);

  return (
    <div className="p-10">
      <HeaderWithNextButton
        title="PROCUREMENT_CREATE.AGREEMENT.HEADER.TITLE"
        action={() => changeActiveTab(7)}
      />
      <FormProvider {...method}>
        <form
          className="container d-flex flex-column gap-6 py-10"
          onSubmit={handleSubmit(onSubmit)}
        >
          <SectionHeader
            title={t("PROCUREMENT_CREATE.AGREEMENT.FORM.VENDOR_INFO.TITLE")}
          />

          <div className="row">
            <div className="col-md-12 col-lg-3">
              <Text weight="semibold" size="sm" color="title">
                {t("PROCUREMENT_CREATE.AGREEMENT.FORM.VENDOR_INFO.NAME.LABEL")}
              </Text>
            </div>
            <div className="col-md-12 col-lg-7">
              <InputText
                name="product_name"
                defaultValue={vendorData?.vendorName || ""}
                disabled
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 col-lg-3">
              <Text weight="semibold" size="sm" color="title">
                {t(
                  "PROCUREMENT_CREATE.AGREEMENT.FORM.VENDOR_INFO.ADDRESS.LABEL",
                )}
              </Text>
            </div>
            <div className="col-md-12 col-lg-7">
              <InputText
                name="product_name"
                defaultValue={vendorData?.address || ""}
                disabled
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 col-lg-3">
              <Text weight="semibold" size="sm" color="title">
                {t(
                  "PROCUREMENT_CREATE.AGREEMENT.FORM.VENDOR_INFO.TRADE_LICENSE_NO.LABEL",
                )}
              </Text>
            </div>
            <div className="col-md-12 col-lg-7">
              <InputText
                name="product_name"
                defaultValue={vendorData?.tradeLicenceNo || ""}
                disabled
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 col-lg-3">
              <Text weight="semibold" size="sm" color="title">
                {t(
                  "PROCUREMENT_CREATE.AGREEMENT.FORM.VENDOR_INFO.CONTACT_NO.LABEL",
                )}
              </Text>
            </div>
            <div className="col-md-12 col-lg-7">
              <InputText
                name="product_name"
                defaultValue={vendorData?.contactNo || ""}
                disabled
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 col-lg-3">
              <Text weight="semibold" size="sm" color="title">
                {t("PROCUREMENT_CREATE.AGREEMENT.FORM.VENDOR_INFO.TYPE.LABEL")}
              </Text>
            </div>
            <div className="col-md-12 col-lg-7">
              <InputText
                name="product_name"
                defaultValue={vendorData?.vendorTypes || ""}
                disabled
              />
            </div>
          </div>

          <ACCAuthorizePersonInfo />
          <ACCWithnessInfo />
          <VendorAuthorizePersonInfo />
          <VendorWithnessInfo />

          <AgreementDocument />

          <div className="row border-bottom py-10">
            {getAccess(roles, "PROCUREMENT_HR") && (
              <div className="d-flex justify-content-end gap-8 col-lg-10">
                <Button
                  fill="outline"
                  type="primary"
                  htmlType="submit"
                  size="sm"
                >
                  {t("PROCUREMENT_CREATE.AGREEMENT.FORM.SAVE_BUTTON_TEXT")}
                </Button>
              </div>
            )}
          </div>
        </form>
        <div className="text-end mt-10">
          <NextButton action={() => changeActiveTab(7)} />
        </div>
      </FormProvider>
    </div>
  );
};

export default Agreement;
